import { GraduationCap, Layers, Brain, Telescope, ShieldCheck, LineChart, type LucideIcon } from "lucide-react";
import { articles, type Article } from "@/lib/articles";

export type AcademyTier = "free" | "standard" | "pro" | "elite";

export const TIER_RANK: Record<AcademyTier, number> = {
  free: 0,
  standard: 1,
  pro: 2,
  elite: 3,
};

export const TIER_LABEL: Record<AcademyTier, string> = {
  free: "Free",
  standard: "Standard",
  pro: "Pro",
  elite: "Elite",
};

export type AcademyLesson = Pick<Article, "slug" | "title">;

export type AcademyModule = {
  id: string;
  number: number;
  title: string;
  subtitle: string;
  requiredTier: AcademyTier;
  gradient: string;
  icon: LucideIcon;
  lessons: AcademyLesson[];
};

const bySlug = (slug: string): Article | undefined =>
  articles.find((a) => a.slug === slug);

const pick = (slugs: string[]): Article[] =>
  slugs.map(bySlug).filter((a): a is Article => Boolean(a));

export const modules: AcademyModule[] = [
  {
    id: "smc-foundation",
    number: 1,
    title: "The SMC Foundation",
    subtitle:
      "Build your bedrock, market structure, order blocks, FVGs and the language of Smart Money.",
    requiredTier: "free",
    gradient: "from-gold-muted via-card to-emerald-muted",
    icon: GraduationCap,
    lessons: pick([
      "smc-gold-trading-strategy",
      "smc-vs-indicators-gold-trading",
      "amd-pattern-gold-daily-cycles",
    ]),
  },
  {
    id: "advanced-liquidity",
    number: 2,
    title: "Advanced Liquidity & Inducement",
    subtitle:
      "Track institutional liquidity raids, inducement traps, and the macro narrative driving XAU/USD.",
    requiredTier: "standard",
    gradient: "from-amber-500/20 via-card to-gold-muted",
    icon: Layers,
    lessons: pick([
      "liquidity-purge-eqh-eql-gold",
      "inducement-trap-idm-smc",
      "high-probability-entry-criteria-gold",
    ]),
  },
  {
    id: "prop-firm-execution",
    number: 3,
    title: "Prop Firm Execution & Psychology",
    subtitle:
      "Pass funded challenges with disciplined execution, drawdown control, and a professional mindset.",
    requiredTier: "pro",
    gradient: "from-emerald-500/20 via-card to-gold-muted",
    icon: Brain,
    lessons: pick([
      "funded-mindset-risk-management",
      "smc-execution-checklist-prop-firm",
      "drawdown-recovery-strategy",
    ]),
  },
  {
    id: "advanced-top-down",
    number: 4,
    title: "Advanced Top-Down Analysis",
    subtitle:
      "Master the art of narrative. Align multiple timeframes to identify high-probability institutional direction.",
    requiredTier: "standard",
    gradient: "from-blue-500/20 via-card to-gold-muted",
    icon: Telescope,
    lessons: [
      { slug: "multi-timeframe-confluence", title: "Multi-Timeframe Confluence" },
      { slug: "daily-bias-mastery", title: "Daily Bias Mastery" },
      { slug: "refined-entry-timeframes", title: "Refined Entry Timeframes" },
    ],
  },
  {
    id: "prop-firm-scaling",
    number: 5,
    title: "Prop Firm Scaling & Risk",
    subtitle:
      "Professional capital management. Strategies to protect and scale your funded accounts consistently.",
    requiredTier: "pro",
    gradient: "from-emerald-500/20 via-card to-blue-500/10",
    icon: ShieldCheck,
    lessons: [
      { slug: "scaling-in-patterns", title: "Scaling In Patterns" },
      { slug: "daily-drawdown-protection", title: "Daily Drawdown Protection" },
      { slug: "payout-consistency-habits", title: "Payout Consistency Habits" },
    ],
  },
  {
    id: "systematic-trader",
    number: 6,
    title: "The Systematic Trader",
    subtitle:
      "Transform your strategy into a mechanical system. Data-driven trading for long-term edge.",
    requiredTier: "elite",
    gradient: "from-purple-500/20 via-card to-gold-muted",
    icon: LineChart,
    lessons: [
      { slug: "backtesting-your-edge", title: "Backtesting Your Edge" },
      { slug: "trade-journaling-for-data", title: "Trade Journaling for Data" },
      { slug: "building-your-trading-plan", title: "Building Your Trading Plan" },
    ],
  },
];

export function canAccessModule(
  userTier: AcademyTier | string | null | undefined,
  required: AcademyTier,
): boolean {
  const t = (userTier ?? "free") as AcademyTier;
  const rank = TIER_RANK[t] ?? 0;
  return rank >= TIER_RANK[required];
}

export function findLesson(
  slug: string,
): { module: AcademyModule; lesson: Article; index: number } | null {
  for (const m of modules) {
    const idx = m.lessons.findIndex((l) => l.slug === slug);
    if (idx >= 0) {
      const lesson = bySlug(slug);
      if (lesson) return { module: m, lesson, index: idx };
    }
  }
  return null;
}

// ---------- Completion (localStorage) ----------

const KEY = (userId: string) => `xaupro:academy:completed:${userId}`;

export function getCompleted(userId: string | undefined): Set<string> {
  if (!userId || typeof window === "undefined") return new Set();
  try {
    const raw = localStorage.getItem(KEY(userId));
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return new Set(arr);
  } catch {
    return new Set();
  }
}

export function setCompleted(userId: string | undefined, slugs: Set<string>) {
  if (!userId || typeof window === "undefined") return;
  localStorage.setItem(KEY(userId), JSON.stringify([...slugs]));
}

export function toggleCompleted(
  userId: string | undefined,
  slug: string,
  done: boolean,
): Set<string> {
  const s = getCompleted(userId);
  if (done) s.add(slug);
  else s.delete(slug);
  setCompleted(userId, s);
  return s;
}
